import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
  useDisclosure
} from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  type Appointment,
  type AppointmentSettings,
  type AppointmentStatus
} from '../../../../../model/appointment'
import { getAppointmentService } from '../../../../../services/Services'
import {
  clinicDateTimeToTimestamp,
  dateKeyInTimeZone
} from '../../../../../utils/appointmentUtils'
import AppointmentModal from '../../../Calendar/components/AppointmentModal'

interface Props {
  ownerId: string
  actorId: string
  settings: AppointmentSettings
}

const STATUS_COLORS: Record<AppointmentStatus, string> = {
  scheduled: 'blue.500',
  confirmed: 'green.500',
  'checked-in': 'purple.500',
  completed: 'gray.400',
  cancelled: 'red.400',
  'no-show': 'orange.400'
}

function shiftDateKey(dateKey: string, offset: number): string {
  const date = new Date(`${dateKey}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}

function MiniCalendar({ ownerId, actorId, settings }: Props) {
  const { formatDate, formatMessage, formatTime } = useIntl()
  const appointmentDrawer = useDisclosure()
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null)
  const { timezone } = settings
  const todayKey = dateKeyInTimeZone(Date.now(), timezone)
  const [selectedDate, setSelectedDate] = useState(todayKey)
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setSelectedDate(todayKey)
  }, [timezone, todayKey])

  useEffect(() => {
    setLoading(true)
    const nextDate = shiftDateKey(selectedDate, 1)
    return getAppointmentService().subscribeToRange(
      ownerId,
      clinicDateTimeToTimestamp(selectedDate, '00:00', timezone),
      clinicDateTimeToTimestamp(nextDate, '00:00', timezone),
      (updated) => {
        setAppointments(updated)
        setLoading(false)
      },
      () => {
        setAppointments([])
        setLoading(false)
      }
    )
  }, [ownerId, selectedDate, timezone])

  const sortedAppointments = useMemo(
    () => [...appointments].sort((left, right) => left.start - right.start),
    [appointments]
  )
  const visibleAppointments = sortedAppointments.slice(0, 3)
  const remainingCount = Math.max(0, sortedAppointments.length - 3)
  const isToday = selectedDate === todayKey
  const selectedTimestamp = clinicDateTimeToTimestamp(
    selectedDate,
    '12:00',
    timezone
  )

  return (
    <Box
      position="relative"
      h="full"
      minH="238px"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      p={5}
    >
      <Flex align="center" justify="space-between" mb={2.5} wrap="wrap" gap={3}>
        <HStack spacing={2}>
          <Flex
            boxSize={8}
            alignItems="center"
            justify="center"
            borderRadius="8px"
            bg="gray.100"
            color="gray.700"
          >
            <CalendarDaysIcon width={16} />
          </Flex>
          <Box>
            <HStack spacing={1.5}>
              <Text fontSize="sm" fontWeight="700" color="gray.900">
                {formatMessage({ id: 'dashboard.appointments' })}
              </Text>
              <Badge
                borderRadius="full"
                bg="gray.100"
                color="gray.600"
                fontSize="9px"
              >
                {sortedAppointments.length}
              </Badge>
            </HStack>
            <Text
              as={Link}
              to={`/calendar?date=${selectedDate}`}
              display="block"
              fontSize="10px"
              color="gray.500"
              _hover={{ color: 'gray.900', textDecoration: 'underline' }}
            >
              {formatMessage({ id: 'dashboard.viewCalendar' })}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={0.5}>
          <IconButton
            aria-label={formatMessage({ id: 'dashboard.previousDay' })}
            icon={<ChevronLeftIcon />}
            size="xs"
            variant="ghost"
            onClick={() => {
              setSelectedDate((current) => shiftDateKey(current, -1))
            }}
          />
          <Button
            minW="128px"
            size="xs"
            variant="ghost"
            fontWeight="600"
            onClick={() => {
              setSelectedDate(todayKey)
            }}
          >
            {isToday
              ? formatMessage({ id: 'dashboard.today' })
              : formatDate(selectedTimestamp, {
                  timeZone: timezone,
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
          </Button>
          <IconButton
            aria-label={formatMessage({ id: 'dashboard.nextDay' })}
            icon={<ChevronRightIcon />}
            size="xs"
            variant="ghost"
            onClick={() => {
              setSelectedDate((current) => shiftDateKey(current, 1))
            }}
          />
        </HStack>
      </Flex>

      <VStack spacing={1} align="stretch">
        {visibleAppointments.map((appointment) => (
          <Button
            key={appointment.id}
            aria-label={formatMessage(
              { id: 'dashboard.openAppointment' },
              { name: appointment.patientName }
            )}
            variant="unstyled"
            display="flex"
            w="full"
            h="auto"
            minH="45px"
            px={2.5}
            py={2}
            alignItems="center"
            gap={2.5}
            borderRadius="8px"
            bg="gray.50"
            textAlign="start"
            transition="background 0.15s ease, box-shadow 0.15s ease"
            _hover={{ bg: 'gray.100' }}
            _focusVisible={{
              boxShadow: '0 0 0 2px var(--chakra-colors-blue-400)'
            }}
            onClick={() => {
              setSelectedAppointment(appointment)
              appointmentDrawer.onOpen()
            }}
          >
            <Box
              boxSize="7px"
              flexShrink={0}
              borderRadius="full"
              bg={STATUS_COLORS[appointment.status]}
            />
            <Box minW={0} flex={1}>
              <Text fontSize="xs" fontWeight="650" noOfLines={1}>
                {appointment.patientName}
              </Text>
              <Text fontSize="10px" color="gray.500">
                {formatMessage({
                  id: `calendar.status.${appointment.status}`
                })}
              </Text>
            </Box>
            <Text
              flexShrink={0}
              fontSize="xs"
              fontWeight="600"
              color="gray.600"
            >
              {formatTime(appointment.start, {
                timeZone: timezone,
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Text>
          </Button>
        ))}

        {!loading && visibleAppointments.length === 0 && (
          <Flex minH="139px" align="center" justify="center">
            <Text fontSize="xs" color="gray.500">
              {formatMessage({ id: 'dashboard.noAppointmentsOnDay' })}
            </Text>
          </Flex>
        )}
      </VStack>

      {remainingCount > 0 && (
        <Text
          as={Link}
          to={`/calendar?date=${selectedDate}`}
          display="block"
          mt={1.5}
          textAlign="center"
          fontSize="10px"
          fontWeight="600"
          color="gray.500"
          _hover={{ color: 'gray.900' }}
        >
          {formatMessage(
            { id: 'dashboard.moreAppointments' },
            { count: remainingCount }
          )}
        </Text>
      )}

      {loading && (
        <Flex
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          bg="whiteAlpha.800"
          borderRadius="10px"
        >
          <Spinner size="sm" />
        </Flex>
      )}

      {selectedAppointment && (
        <AppointmentModal
          isOpen={appointmentDrawer.isOpen}
          onClose={appointmentDrawer.onClose}
          ownerId={ownerId}
          actor={{ uid: actorId }}
          settings={settings}
          appointment={selectedAppointment}
        />
      )}
    </Box>
  )
}

export default MiniCalendar
